import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { ApprovalActionsService } from 'app/core/approvals/approval-actions.service';
import { ApprovalInboxItemResponse } from 'app/core/approvals/approval-inbox.models';
import { ApprovalInboxService } from 'app/core/approvals/approval-inbox.service';
import { PermissionService } from 'app/core/permission/permission.service';
import { AmountPipe } from 'app/core/pipes/custom-currency.pipe';
import { CompanyDatePipe } from 'app/core/pipes/company-date.pipe';
import { ApprovalRemarksDialogComponent } from 'app/modules/accounting/treasury/shared/approval-remarks-dialog/approval-remarks-dialog.component';
import { ToastrService } from 'ngx-toastr';
import {
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  interval,
  lastValueFrom,
  merge,
  of,
  startWith,
  switchMap,
} from 'rxjs';

/**
 * Header entry for the unified approval inbox (next to notifications).
 * Opens a panel overlay — same interaction pattern as notifications.
 */
@Component({
  selector: 'approval-inbox-button',
  standalone: true,
  templateUrl: './approval-inbox-button.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'approvalInboxButton',
  imports: [
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    AmountPipe,
    CompanyDatePipe,
  ],
})
export class ApprovalInboxButtonComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly inbox = inject(ApprovalInboxService);
  private readonly actions = inject(ApprovalActionsService);
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly toast = inject(ToastrService);
  private readonly permissions = inject(PermissionService);

  @ViewChild('inboxOrigin') private inboxOrigin: MatButton;
  @ViewChild('inboxPanel') private inboxPanel: TemplateRef<unknown>;

  readonly isSuperAdmin = this.permissions.isSuperAdmin();

  pendingCount = 0;
  items: ApprovalInboxItemResponse[] = [];
  filterKeyword = '';
  loading = false;

  private overlayRef: OverlayRef | null = null;
  private readonly keywordSearch$ = new Subject<string>();

  ngOnInit(): void {
    merge(interval(60_000).pipe(startWith(0)), this.inbox.changed$)
      .pipe(
        switchMap(() =>
          this.inbox.getPendingCount().pipe(catchError(() => of({ success: true, data: 0 })))
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((resp) => {
        this.pendingCount = Number(resp?.data ?? 0) || 0;
        this.changeDetectorRef.markForCheck();
      });

    this.keywordSearch$
      .pipe(debounceTime(350), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadItems());
  }

  ngOnDestroy(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  openPanel(): void {
    if (!this.inboxPanel || !this.inboxOrigin) {
      return;
    }

    if (!this.overlayRef) {
      this.createOverlay();
    }

    this.overlayRef!.attach(new TemplatePortal(this.inboxPanel, this.viewContainerRef));
    this.changeDetectorRef.detectChanges();
    this.loadItems();
  }

  closePanel(): void {
    this.overlayRef?.detach();
  }

  refresh(): void {
    this.loadItems();
    this.refreshCount();
  }

  onKeywordChange(): void {
    this.keywordSearch$.next(this.filterKeyword.trim());
  }

  clearKeyword(): void {
    this.filterKeyword = '';
    this.loadItems();
  }

  loadItems(): void {
    this.loading = true;
    this.changeDetectorRef.markForCheck();

    lastValueFrom(
      this.inbox.getPending({
        keyword: this.filterKeyword.trim() || undefined,
      })
    )
      .then((resp) => {
        const data = resp?.data;
        this.items = Array.isArray(data) ? data : [];
        if (!this.filterKeyword.trim()) {
          this.pendingCount = this.items.length;
        }
        this.loading = false;
        this.changeDetectorRef.markForCheck();
      })
      .catch((err: HttpErrorResponse) => {
        this.loading = false;
        this.toast.error(err?.error?.message || 'Failed to load approval inbox');
        this.changeDetectorRef.markForCheck();
      });
  }

  canAct(item: ApprovalInboxItemResponse | null | undefined): boolean {
    if (!item) return false;
    if (!this.isSuperAdmin) return true;
    if (item.canActAsAdmin) return true;
    return item.steps?.some((s) => s.isCurrentStep && s.canAct) ?? false;
  }

  openDocument(item: ApprovalInboxItemResponse): void {
    const url = item.navigationUrl?.trim();
    this.closePanel();

    if (url) {
      if (url.startsWith('http://') || url.startsWith('https://')) {
        window.open(url, '_blank');
        return;
      }
      void this.router.navigateByUrl(url.startsWith('/') ? url : `/${url}`);
      return;
    }

    this.toast.info('No document link available for this item');
  }

  inboxStepLabel(item: ApprovalInboxItemResponse | null | undefined): string {
    return (item?.roleName || item?.stepTypeName || '').trim();
  }

  openFullInbox(): void {
    this.closePanel();
    void this.router.navigateByUrl('/approvals/inbox');
  }

  approve(item: ApprovalInboxItemResponse): void {
    if (!this.canAct(item)) return;
    const stepLabel = this.inboxStepLabel(item);
    const action = /reviewer/i.test(stepLabel) ? 'Review' : 'Approve';
    this.dialog
      .open(ApprovalRemarksDialogComponent, {
        width: '480px',
        data: {
          title: action,
          description: `${action} ${item.documentNumber || 'this document'}?`,
          actionLabel: action,
          actionColor: 'primary',
        },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((comments: string | null) => {
        if (comments === null) return;
        this.loading = true;
        this.changeDetectorRef.markForCheck();
        lastValueFrom(this.actions.approve(item, comments))
          .then((resp: unknown) => {
            const message =
              resp && typeof resp === 'object' && 'message' in resp
                ? String((resp as { message?: string }).message || '')
                : '';
            this.toast.success(message || 'Approved');
            this.loadItems();
            this.refreshCount();
          })
          .catch((err: HttpErrorResponse) => {
            this.loading = false;
            this.toast.error(err?.error?.message || 'Approval failed');
            this.changeDetectorRef.markForCheck();
          });
      });
  }

  reject(item: ApprovalInboxItemResponse): void {
    if (!this.canAct(item)) return;
    this.dialog
      .open(ApprovalRemarksDialogComponent, {
        width: '480px',
        data: {
          title: 'Reject',
          description: `Reject ${item.documentNumber || 'this document'}?`,
          actionLabel: 'Reject',
          actionColor: 'warn',
          requireRemarks: true,
        },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((reason: string | null) => {
        if (reason === null) return;
        this.loading = true;
        this.changeDetectorRef.markForCheck();
        lastValueFrom(this.actions.reject(item, reason))
          .then((resp: unknown) => {
            const message =
              resp && typeof resp === 'object' && 'message' in resp
                ? String((resp as { message?: string }).message || '')
                : '';
            this.toast.success(message || 'Rejected');
            this.loadItems();
            this.refreshCount();
          })
          .catch((err: HttpErrorResponse) => {
            this.loading = false;
            this.toast.error(err?.error?.message || 'Rejection failed');
            this.changeDetectorRef.markForCheck();
          });
      });
  }

  trackByRow = (_index: number, item: ApprovalInboxItemResponse): string =>
    `${item.companyId || ''}-${item.transactionType}-${item.approvalId}`;

  private refreshCount(): void {
    this.inbox
      .getPendingCount()
      .pipe(
        catchError(() => of({ success: true, data: 0 })),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((resp) => {
        this.pendingCount = Number(resp?.data ?? 0) || 0;
        this.changeDetectorRef.markForCheck();
      });
  }

  private createOverlay(): void {
    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'fuse-backdrop-on-mobile',
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.inboxOrigin._elementRef.nativeElement)
        .withLockedPosition(true)
        .withPush(true)
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom',
          },
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
          },
          {
            originX: 'end',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'bottom',
          },
        ]),
    });

    this.overlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.overlayRef?.detach();
      });
  }
}
